import {CopilotAvatar} from '@github-ui/copilot-avatar'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {usePortalTooltip} from '@github-ui/portal-tooltip/use-portal-tooltip'
import {UserNameDisplayContext} from '@github-ui/profile-reference/UserNameDisplayContext'
import {type TestIdProps, testIdProps} from '@github-ui/test-id-props'
import {AvatarStack} from '@primer/react'
import {clsx} from 'clsx'
import {use, useCallback, useRef} from 'react'

import type {IAssignee, User} from '../../api/common-contracts'
import {isCopilot} from '../../helpers/assignee-helper'
import {joinOxford} from '../../helpers/join-oxford'
import {useSearch} from '../filter-bar/search-context'
import styles from './assignee-stack.module.css'

type Props = TestIdProps & {
  assignees?: Array<User>
  alignRight?: boolean
  style?: React.CSSProperties
  avatarProps?: Partial<React.HTMLAttributes<HTMLElement>>
}

type ClickableAvatarProps = {
  assignee: IAssignee
} & Partial<React.HTMLAttributes<HTMLElement>>

// Note that extra props are passed in by the <AvatarStack> component parent
const ClickableAvatar = ({assignee, onFocus, className, ...props}: ClickableAvatarProps) => {
  const {toggleFilter} = useSearch()
  const displayMode = use(UserNameDisplayContext)

  const displayName =
    displayMode === 'name_handle' && assignee.name?.trim() ? `${assignee.name} (${assignee.login})` : assignee.login

  const toggleFilterOnClick = useCallback(
    (event: React.MouseEvent<HTMLImageElement>) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (!event.shiftKey && !event.metaKey) {
        // If the shift or meta key is held, we are assuming the user wants to
        // select multiple cards, in which case filtering would break
        // expectation and cause them to lose their selections.
        toggleFilter('assignee', assignee.login)
      }
    },
    [toggleFilter, assignee.login],
  )
  const toggleFilterOnEnter = useCallback(
    (event: React.KeyboardEvent<HTMLImageElement>) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (event.key === 'Enter') {
        toggleFilter('assignee', assignee.login)
      }
    },
    [toggleFilter, assignee.login],
  )

  const avatar = useRef<HTMLImageElement>(null)
  const [contentProps, tooltip] = usePortalTooltip({
    contentRef: avatar,
    'aria-label': displayName,
  })

  return (
    <span
      onClick={toggleFilterOnClick}
      onFocus={onFocus}
      onKeyDown={toggleFilterOnEnter}
      tabIndex={0}
      className={clsx(className, 'pc-AvatarItem memex-AvatarItem', styles.clickableAvatarWrapper)}
      role="button"
      aria-label={displayName}
      {...testIdProps('AvatarItemFilterButton')}
    >
      {isCopilot(assignee.login) ? (
        <CopilotAvatar />
      ) : (
        <GitHubAvatar
          {...props}
          ref={avatar}
          loading="lazy"
          alt={assignee.login}
          src={assignee.avatarUrl}
          {...contentProps}
        />
      )}
      {tooltip}
    </span>
  )
}

function getAssigneesText(assignees: Array<User>, displayMode: 'handle' | 'name_handle', opts = {reverse: false}) {
  if (assignees.length === 0) return 'none'
  const names = assignees.map(a => {
    if (displayMode === 'name_handle' && a.name?.trim()) {
      return `${a.name} (${a.login})`
    }
    return a.login
  })
  return joinOxford(opts.reverse ? names.reverse() : names)
}

export const AssigneeStack = ({assignees, alignRight = false, avatarProps, ...props}: Props) => {
  const displayMode = use(UserNameDisplayContext)

  if (assignees === undefined || assignees.length === 0) {
    return null
  }

  return (
    <figure className={styles.assigneeStackWrapper} {...props}>
      <figcaption className="sr-only">
        Assignees: {getAssigneesText(assignees, displayMode, {reverse: alignRight})}
      </figcaption>
      <AvatarStack alignRight={alignRight} className={styles.AvatarStack}>
        {assignees.map(assignee => (
          <ClickableAvatar key={assignee.id} assignee={assignee} {...avatarProps} />
        ))}
      </AvatarStack>
    </figure>
  )
}

try{ ClickableAvatar.displayName ||= 'ClickableAvatar' } catch {}
try{ AssigneeStack.displayName ||= 'AssigneeStack' } catch {}